import { useCallback, useEffect, useRef } from "react";
import { useNavigation, useRevalidator } from "react-router";

/**
 * Returns a `revalidate()` wrapper that no-ops while the router is busy.
 *
 * WHY (incident 2026-07-06): React Router's `revalidate()` aborts any in-flight
 * navigation before it starts. The `/sandbox` loader throws `redirect()` to
 * `/login` / `/logout` when the auth token can't be refreshed (encoded as an
 * HTTP 202 on the `.data` request). If a caller invokes `revalidate()` again
 * while that redirect navigation is still pending, it CANCELS the redirect and
 * re-requests the *current* route's data (`/sandbox.data`) instead. The page
 * mounts several unthrottled `revalidate()` callers (feedback refresh, the
 * points-earned WS subscription, a `levelChange` listener), so once the token
 * is in a flapping state this becomes an unbounded loop that never commits the
 * redirect — one browser fired ~180 req/s for ~5 min and saturated the shared
 * web.space process, degrading every tenant.
 *
 * Gating every revalidate on a FULLY IDLE router (revalidator idle AND no
 * pending navigation) lets a pending redirect actually navigate away, which
 * unmounts the sandbox page and terminates the loop on the first redirect.
 *
 * The idle check is read through a ref so callers wired into event listeners /
 * subscription `onData` / `setTimeout` observe the latest router state at fire
 * time rather than a stale closure. The returned function has a stable identity
 * so it is safe to use in effect dependency arrays.
 */
export function useIdleRevalidate(): () => void {
  const revalidator = useRevalidator();
  const navigation = useNavigation();

  const isIdleRef = useRef(true);
  const revalidateRef = useRef(revalidator.revalidate);

  useEffect(() => {
    isIdleRef.current =
      revalidator.state === "idle" && navigation.state === "idle";
    revalidateRef.current = revalidator.revalidate;
  }, [revalidator.state, revalidator.revalidate, navigation.state]);

  return useCallback(() => {
    if (!isIdleRef.current) return;
    revalidateRef.current();
  }, []);
}
